import { useQuery } from '@tanstack/react-query';
import { Box, Flex, Heading, Input, Stack, Text, chakra } from '@chakra-ui/react';
import { ExternalLink, Loader2, Search, Target } from 'lucide-react';
import { useState } from 'react';

import { getMarkets } from '../api/client';
import type { Market } from '../api/types';
import { categoryFilters, type CategoryFilter, type DashboardFilters } from '../lib/filters';
import { money, relativeTime } from '../lib/format';
import { polymarketEventUrlFromEventSlug } from '../lib/polymarket';
import { SelectMenu, type SelectMenuOption } from './SelectMenu';

const categoryOptions: readonly SelectMenuOption<CategoryFilter>[] = categoryFilters.map((item) => ({
  value: item,
  label: item === 'all' ? 'All' : item.slice(0, 1).toUpperCase() + item.slice(1),
}));

export function MarketsView({ filters }: { filters: DashboardFilters }) {
  const [search, setSearch] = useState(filters.query);
  const [category, setCategory] = useState<DashboardFilters['category']>(filters.category);
  const marketsQuery = useQuery({
    queryKey: ['markets', search.trim(), category],
    queryFn: ({ signal }) => getMarkets({ search, category, limit: 100 }, { signal }),
    refetchInterval: 30_000,
  });
  const markets = marketsQuery.data?.items ?? [];

  return (
    <Stack as="section" mt="8" gap="5">
      <Flex
        flexDir={{ base: 'column', xl: 'row' }}
        gap="3"
        align={{ xl: 'flex-end' }}
        justify={{ xl: 'space-between' }}
      >
        <Stack gap="3">
          <Flex align="center" gap="3">
            <Box color="signal.blue">
              <Target size={28} />
            </Box>
            <Heading as="h1" fontSize="3xl" fontWeight="semibold" letterSpacing="tight">
              Markets
            </Heading>
          </Flex>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="text.muted">
            Real discovered market metadata from the backend.
          </Text>
        </Stack>
        {marketsQuery.isFetching ? (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            borderRadius="oqPanel"
            px="4"
            py="2.5"
            layerStyle="glassPanel"
          >
            <Box as="span" display="inline-flex" alignItems="center">
              <Loader2 className="oq-spin" size={16} />
            </Box>
            <Text as="span" fontSize="sm" color="text.muted">
              Refreshing
            </Text>
          </Box>
        ) : null}
      </Flex>

      <Box
        as="section"
        mt="5"
        display="grid"
        gap="2"
        borderRadius="oqPanel"
        p="2"
        gridTemplateColumns={{ base: '1fr', md: 'minmax(260px,1fr) 220px' }}
        layerStyle="glassPanel"
      >
        <Flex
          as="label"
          align="center"
          gap="3"
          borderRadius="oqControl"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.100"
          px="4"
        >
          <Box color="text.muted">
            <Search size={16} />
          </Box>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search markets..."
            variant="flushed"
            h="11"
            w="full"
            bg="transparent"
            fontSize="sm"
            color="text.primary"
            border="none"
            _placeholder={{ color: 'text.muted', opacity: 0.7 }}
          />
        </Flex>
        <SelectMenu label="Category" value={category} options={categoryOptions} onChange={setCategory} />
      </Box>

      <Box as="section" mt="5" overflow="hidden" borderRadius="oqPanel" layerStyle="glassPanel">
        {marketsQuery.isError ? (
          <MarketEmptyState
            title="Markets unavailable."
            detail="The backend did not return the discovered markets list."
          />
        ) : markets.length === 0 ? (
          <MarketEmptyState
            title="No markets found."
            detail="No discovered backend markets match the current search and category."
          />
        ) : (
          <MarketsTable markets={markets} />
        )}
      </Box>
    </Stack>
  );
}

function MarketsTable({ markets }: { markets: Market[] }) {
  return (
    <Box className="scrollbar-soft" overflowX="auto" overflowY="hidden">
      <chakra.table w="full" minW="960px" borderCollapse="collapse" fontSize="sm">
        <chakra.thead bg="surface.panel">
          <chakra.tr
            borderBottomWidth="1px"
            borderColor="border.subtle"
            textAlign="left"
            fontSize="xs"
            fontWeight="medium"
            color="text.muted"
          >
            <chakra.th px="5" py="3">
              Market
            </chakra.th>
            <chakra.th px="3" py="3">
              Category
            </chakra.th>
            <chakra.th px="3" py="3">
              Event Slug
            </chakra.th>
            <chakra.th px="3" py="3">
              State
            </chakra.th>
            <chakra.th px="3" py="3">
              Volume 24h
            </chakra.th>
            <chakra.th px="3" py="3">
              Liquidity
            </chakra.th>
            <chakra.th px="3" py="3">
              Updated
            </chakra.th>
          </chakra.tr>
        </chakra.thead>
        <chakra.tbody>
          {markets.map((market) => {
            const url = polymarketEventUrlFromEventSlug(market.event_slug);
            return (
              <chakra.tr
                key={market.id}
                borderBottomWidth="1px"
                borderColor="border.subtle"
                transition="all 0.2s"
                _hover={{ bg: 'whiteAlpha.50' }}
              >
                <chakra.td maxW="420px" px="5" py="3">
                  <Flex align="flex-start" gap="2">
                    <Box minW="0">
                      <Text fontWeight="medium" lineHeight="snug" color="text.primary" lineClamp={2}>
                        {market.question}
                      </Text>
                      <Text mt="1" fontSize="xs" color="text.muted">
                        {market.id}
                      </Text>
                    </Box>
                    {url ? (
                      <chakra.a
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label="Open market on Polymarket"
                        mt="0.5"
                        flexShrink="0"
                        color="text.muted"
                        transition="all 0.2s"
                        _hover={{ color: 'signal.blue' }}
                      >
                        <ExternalLink size={16} />
                      </chakra.a>
                    ) : null}
                  </Flex>
                </chakra.td>
                <chakra.td whiteSpace="nowrap" px="3" py="3" color="text.muted">
                  {market.category || 'uncategorized'}
                </chakra.td>
                <chakra.td maxW="220px" truncate px="3" py="3" color="text.muted">
                  {market.event_slug || '-'}
                </chakra.td>
                <chakra.td px="3" py="3">
                  <StateBadge market={market} />
                </chakra.td>
                <chakra.td whiteSpace="nowrap" px="3" py="3" color="text.primary">
                  {money(market.volume_24h)}
                </chakra.td>
                <chakra.td whiteSpace="nowrap" px="3" py="3" color="text.primary">
                  {money(market.liquidity)}
                </chakra.td>
                <chakra.td whiteSpace="nowrap" px="3" py="3" color="text.muted">
                  {relativeTime(market.updated_at)}
                </chakra.td>
              </chakra.tr>
            );
          })}
        </chakra.tbody>
      </chakra.table>
    </Box>
  );
}

function StateBadge({ market }: { market: Market }) {
  const label = market.closed ? 'Resolved' : market.active ? 'Active' : 'Inactive';
  const tone = market.closed
    ? { bg: 'surface.control', borderColor: 'border.subtle', color: 'text.muted' }
    : market.active
      ? { bg: 'signal.good/10', borderColor: 'signal.good/25', color: 'signal.good' }
      : { bg: 'signal.warn/10', borderColor: 'signal.warn/25', color: 'signal.warn' };
  return (
    <Box
      as="span"
      display="inline-block"
      whiteSpace="nowrap"
      borderRadius="oqControl"
      borderWidth="1px"
      px="2.5"
      py="1"
      fontSize="xs"
      fontWeight="semibold"
      bg={tone.bg}
      borderColor={tone.borderColor}
      color={tone.color}
    >
      {label}
    </Box>
  );
}

function MarketEmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <Box display="grid" minH="360px" placeItems="center" px="5" py="10">
      <Box maxW="md" textAlign="center">
        <Box
          mx="auto"
          display="grid"
          h="14"
          w="14"
          placeItems="center"
          borderRadius="oqPanel"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.200"
          color="signal.blue"
        >
          <Target size={28} />
        </Box>
        <Heading as="h3" mt="5" fontSize="xl" fontWeight="semibold" color="text.primary">
          {title}
        </Heading>
        <Text mt="2" fontSize="sm" lineHeight="6" color="text.muted">
          {detail}
        </Text>
      </Box>
    </Box>
  );
}